import { DatePicker, Form } from 'antd';
import { SharedTimeProps } from 'rc-picker/lib/panels/TimePanel';
import { Rule } from 'antd/lib/form';
import classNames from 'classnames';
import Text from 'components/Text/Text';
import moment, { Moment } from 'moment';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import style from './DateInput.module.scss';

export interface IDateInput {
	name: string;
	label: string;
	onChange: (value: string) => void;
	getRawFormat?: boolean;
	testId?: string;
	placeholder?: string;
	format?: string;
	timeFormat?: string;
	resetSeconds?: boolean;
	validationRules?: Rule[];
	containerClassName?: string;
	labelClassName?: string;
	inputClassName?: string;
}

const DateInput: React.FC<IDateInput> = ({
	name,
	label,
	onChange,
	getRawFormat = false,
	testId,
	placeholder = 'datePickerPlaceholder',
	format = 'DD.MM.YYYY / HH:mm',
	timeFormat = 'HH:mm',
	resetSeconds = false,
	validationRules,
	containerClassName,
	labelClassName,
	inputClassName,
}) => {
	const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
	const parentNodeRef = useRef<null | HTMLSpanElement>(null);
	const { t } = useTranslation('form');

	const containerClasses = classNames(style.container, containerClassName);
	const labelClasses = classNames(style.label, labelClassName);
	const inputClasses = classNames(style.input, inputClassName);

	const isRequired = validationRules?.some((rule) => {
		if (typeof rule === 'object') {
			return rule?.required;
		}
		return false;
	});

	const handleChange = (value: Moment | null, dateString: string) => {
		const isoValue = value?.set(resetSeconds ? { seconds: 0, milliseconds: 0 } : {}).toISOString(); // reset milliseconds
		const formattedValue = getRawFormat ? isoValue : dateString;

		formattedValue && onChange(formattedValue);
	};

	const disablePastDates = (selectedDate: Moment) => {
		const today = moment();
		const isBeforeToday = moment(selectedDate).isBefore(today, 'day');
		return isBeforeToday;
	};

	const getTimeOptions = (): boolean | SharedTimeProps<Moment> => {
		const nowDay = moment().date();
		const selectedDay = selectedDate?.date();
		const nowTime = moment().hour();
		const selectedTime = selectedDate?.hour();
		const prevHour = moment().subtract(1, 'hours').hours();
		const prevMinute = moment().minutes();

		const createRange = (start: number, end: number) => {
			const result = [];
			for (let i = start; i <= end; i++) {
				result.push(i);
			}
			return result;
		};

		const disabledAllTimes = {
			disabledHours: () => createRange(0, 24),
			disabledMinutes: () => createRange(0, 60),
		};

		const disabledPastTimes = {
			disabledHours: () => (nowDay === selectedDay ? createRange(0, prevHour) : []),
			disabledMinutes: () =>
				nowTime === selectedTime && nowDay === selectedDay ? createRange(0, prevMinute) : [],
		};

		const disabledTimes = !selectedDate ? disabledAllTimes : disabledPastTimes;

		return {
			format: timeFormat,
			...disabledTimes,
		};
	};

	return (
		<div className={containerClasses} data-test={testId}>
			<div className={style.labelWrapper}>
				<Text
					className={labelClasses}
					translationNameSpace="form"
					translationKey={label}
					size="xs"
					theme="neutralLight"
				/>
				{!!isRequired && <Text translationKey="*" size="xs" theme="neutralLight" display="inline" />}
			</div>
			<span ref={parentNodeRef} style={{ position: 'relative' }} />
			<Form.Item name={name} rules={validationRules}>
				<DatePicker
					className={inputClasses}
					placeholder={t(placeholder)}
					format={format}
					showTime={timeFormat ? getTimeOptions() : false}
					onChange={handleChange}
					onSelect={setSelectedDate}
					getPopupContainer={() => parentNodeRef.current as HTMLSpanElement}
					disabledDate={disablePastDates}
				/>
			</Form.Item>
		</div>
	);
};

export default React.memo(DateInput);
